import { atom, type Atom } from 'jotai';
import { unwrap } from 'jotai/utils';

export type Loadable<Value> =
  { state: 'loading' } | { state: 'hasData'; data: Value } | { state: 'hasError'; error: unknown };

const LOADING: Loadable<never> = { state: 'loading' };
const cache = new WeakMap<Atom<unknown>, Atom<Loadable<unknown>>>();

/**
 * Wraps an async atom so its value is represented as a Loadable
 * discriminated union instead of triggering Suspense.
 *
 * Drop-in replacement for the deprecated `loadable` from `jotai/utils`.
 * Uses `unwrap` internally per the recommended migration path.
 */
export function loadable<Value>(anAtom: Atom<Value>): Atom<Loadable<Awaited<Value>>> {
  const existing = cache.get(anAtom);
  if (existing) return existing as Atom<Loadable<Awaited<Value>>>;

  const unwrappedAtom = unwrap(anAtom, () => LOADING as Awaited<Value>);
  const loadableAtom = atom<Loadable<Awaited<Value>>>(get => {
    try {
      const data = get(unwrappedAtom);
      if (data === (LOADING as unknown)) {
        return LOADING as Loadable<Awaited<Value>>;
      }
      return { state: 'hasData', data: data as Awaited<Value> };
    } catch (error) {
      return { state: 'hasError', error };
    }
  });

  cache.set(anAtom, loadableAtom as Atom<Loadable<unknown>>);
  return loadableAtom;
}
